import { store } from '../store';
import {
  setSessions,
  setCurrentSession,
  addSession,
  updateSessionStatus,
  deleteSession as deleteSessionAction,
  deleteSessions as deleteSessionsAction,
  addMessage,
  updateMessageContent,
  setStreaming,
  updateSessionPinned,
  updateSessionTitle,
  enqueuePendingPermission,
  dequeuePendingPermission,
  setConfig,
  clearCurrentSession,
  setQueuedMessageForSession,
} from '../store/slices/coworkSlice';
import type {
  CoworkSession,
  CoworkConfigUpdate,
  CoworkApiConfig,
  CoworkSandboxStatus,
  CoworkSandboxProgress,
  CoworkUserMemoryEntry,
  CoworkMemoryStats,
  CoworkPermissionResult,
  CoworkStartOptions,
  CoworkContinueOptions,
} from '../types/cowork';
import { getTransport, resetTransport } from '../transport';
import type { Transport } from '../transport';

class CoworkService {
  private transport: Transport | null = null;
  private streamListenerCleanups: Array<() => void> = [];
  private initialized = false;

  async init(): Promise<void> {
    if (this.initialized) return;

    // Get WebSocket transport
    this.transport = getTransport();

    // Load initial config
    await this.loadConfig();

    // Load sessions list
    await this.loadSessions();

    // Set up stream listeners
    this.setupStreamListeners();

    this.initialized = true;
  }

  private setupStreamListeners(): void {
    if (!this.transport) return;

    // Clean up any existing listeners
    this.cleanupListeners();

    // Message listener - also check if session exists (for IM-created sessions)
    const messageCleanup = this.transport.on('message', async ({ sessionId, message }) => {
      // Debug: log user messages to check if imageAttachments are preserved
      if (message.type === 'user') {
        const meta = message.metadata as Record<string, unknown> | undefined;
        console.log('[CoworkService] onStreamMessage received user message', {
          sessionId,
          messageId: message.id,
          hasMetadata: !!meta,
          metadataKeys: meta ? Object.keys(meta) : [],
          hasImageAttachments: !!(meta?.imageAttachments),
          imageAttachmentsCount: Array.isArray(meta?.imageAttachments) ? (meta.imageAttachments as unknown[]).length : 0,
        });
      }
      // Check if session exists in current list
      const state = store.getState().cowork;
      const sessionExists = state.sessions.some(s => s.id === sessionId);

      if (!sessionExists) {
        // Session was created by IM or another source, refresh the session list
        await this.loadSessions();
      }

      // A new user turn means this session is actively running again
      // (especially important for IM-triggered turns that do not call continueSession from renderer).
      if (message.type === 'user') {
        store.dispatch(updateSessionStatus({ sessionId, status: 'running' }));
      }

      // Do not force status back to "running" on arbitrary messages.
      // Late stream chunks can arrive after an error/complete event.
      store.dispatch(addMessage({ sessionId, message }));
    });
    this.streamListenerCleanups.push(messageCleanup);

    // Message update listener (for streaming content updates)
    const messageUpdateCleanup = this.transport.on('messageUpdate', ({ sessionId, messageId, content }) => {
      store.dispatch(updateMessageContent({ sessionId, messageId, content }));
    });
    this.streamListenerCleanups.push(messageUpdateCleanup);

    // Permission request listener
    const permissionCleanup = this.transport.on('permissionRequest', ({ sessionId, request }) => {
      store.dispatch(enqueuePendingPermission({
        sessionId,
        toolName: request.toolName,
        toolInput: request.toolInput,
        requestId: request.requestId,
        toolUseId: request.toolUseId ?? null,
      }));
    });
    this.streamListenerCleanups.push(permissionCleanup);

    // Complete listener
    const completeCleanup = this.transport.on('complete', ({ sessionId }) => {
      store.dispatch(updateSessionStatus({ sessionId, status: 'completed' }));

      // Check if there's a queued message for this session and auto-send it
      const state = store.getState().cowork;
      const queuedMessage = state.queuedMessageBySession[sessionId];
      if (queuedMessage && state.currentSessionId === sessionId) {
        // If user is on this session, the component will handle it
        // But we also need to check if the user withdrew the queue
        const wasWithdrawn = state.queueWithdrawnBySession[sessionId];
        if (!wasWithdrawn) {
          // Auto-send the queued message
          console.log('[CoworkService] Auto-sending queued message after stream complete');
          this.continueSession({
            sessionId,
            prompt: queuedMessage.prompt,
            systemPrompt: state.currentSession?.systemPrompt || '',
            activeSkillIds: state.currentSession?.activeSkillIds || [],
          });
        }
        // Clear the queued message
        store.dispatch(setQueuedMessageForSession({ sessionId, message: null }));
      }
    });
    this.streamListenerCleanups.push(completeCleanup);

    // Error listener — the server sends the reason in the payload; surface it
    // instead of only flipping the status badge (which made failures look like
    // the assistant silently did nothing).
    const errorCleanup = this.transport.on('error', ({ sessionId, error }) => {
      store.dispatch(updateSessionStatus({ sessionId, status: 'error' }));
      const detail = (error || '').trim();
      const message = detail
        ? `Assistant failed: ${detail.length > 200 ? `${detail.slice(0, 200)}…` : detail}`
        : 'The assistant failed to respond. See the server log for details.';
      console.error('[CoworkService] Session error:', { sessionId, error });
      window.dispatchEvent(new CustomEvent('app:showToast', {
        detail: { message, type: 'error' },
      }));
    });
    this.streamListenerCleanups.push(errorCleanup);

    // Session status changed listener — server emits this when a session's
    // status is mutated outside the normal runner event lifecycle (e.g. spike
    // alert, email-assistant, clinic-sync HTTP routes). It lets the frontend
    // learn about 'running' / 'completed' / 'error' / 'idle' transitions in
    // real time instead of waiting for the first stream message.
    const statusChangedCleanup = this.transport.onEvent('sessionStatusChanged', (payload: unknown) => {
      const event = payload as { sessionId: string; status: 'idle' | 'running' | 'completed' | 'error' };
      if (event.sessionId && event.status) {
        store.dispatch(updateSessionStatus({ sessionId: event.sessionId, status: event.status }));
      }
    });
    this.streamListenerCleanups.push(statusChangedCleanup);

    // Gmail new email notification
    const gmailCleanup = this.transport.onEvent('gmail:newEmail', (payload: unknown) => {
      const event = payload as { from?: string; subject?: string };
      const from = event.from || 'Unknown';
      const subject = event.subject || '(No subject)';
      const toastMsg = `New email from ${from}: ${subject}`;
      console.log('[CoworkService] Gmail new email:', toastMsg);
      window.dispatchEvent(new CustomEvent('app:showToast', { detail: { message: toastMsg, type: 'info' } }));
    });
    this.streamListenerCleanups.push(gmailCleanup);

    // Session created by server (e.g., email-assistant auto-created by watcher)
    const sessionCreatedCleanup = this.transport.onEvent('cowork:sessionCreated', () => {
      console.log('[CoworkService] Server created a new session, refreshing sessions list');
      this.loadSessions();
    });
    this.streamListenerCleanups.push(sessionCreatedCleanup);

    const sessionsResetCleanup = this.transport.onEvent('sessionsReset', () => {
      console.log('[CoworkService] Sessions were reset by server, refreshing sessions list');
      this.loadSessions();
    });
    this.streamListenerCleanups.push(sessionsResetCleanup);

    // Email sent notification (reply, forward, send)
    const emailSentCleanup = this.transport.onEvent('gmail:emailSent', (payload: unknown) => {
      const event = payload as { type: string; to?: string; subject?: string; messageId?: string };
      const action = event.type === 'reply' ? 'Replied to email' : event.type === 'forward' ? 'Forwarded email' : 'Email sent';
      const detail = event.to ? ` to ${event.to}` : event.subject ? `: ${event.subject}` : '';
      const toastMsg = `${action}${detail}`;
      console.log('[CoworkService] Gmail email sent:', toastMsg);
      window.dispatchEvent(new CustomEvent('app:showToast', { detail: { message: toastMsg, type: 'success' } }));
    });
    this.streamListenerCleanups.push(emailSentCleanup);
  }

  private cleanupListeners(): void {
    this.streamListenerCleanups.forEach(cleanup => cleanup());
    this.streamListenerCleanups = [];
  }

  async loadSessions(): Promise<void> {
    const result = await this.transport?.listSessions();
    console.log('[CoworkService] loadSessions result:', result);
    
    // Handle both response formats: { sessions } and { data: { sessions } }
    const sessions = (result as any)?.sessions || result?.data?.sessions;
    if (result?.success && sessions) {
      store.dispatch(setSessions(sessions));
      console.log('[CoworkService] Loaded sessions:', sessions.length);
    } else {
      console.error('[CoworkService] Failed to load sessions:', result);
    }
  }

  async loadConfig(): Promise<void> {
    const result = await this.transport?.getConfig();
    console.log('[CoworkService] loadConfig result:', result);
    
    // Handle both response formats: { config } and { data: { config } }
    const config = (result as any)?.config || result?.data?.config;
    if (result?.success && config) {
      store.dispatch(setConfig(config));
      console.log('[CoworkService] Loaded config');
    } else {
      console.error('[CoworkService] Failed to load config:', result);
    }
  }

  async startSession(options: CoworkStartOptions): Promise<CoworkSession | null> {
    if (!this.transport) {
      console.error('Transport not initialized');
      return null;
    }

    store.dispatch(setStreaming(true));

    try {
      const result = await this.transport.startSession(options);
      console.log('[CoworkService] startSession result:', result);
      
      // WebSocket transport returns handler result directly via response.data
      // Handler returns { success: true, session } or { success: false, error }
      const resultObj = result as any;
      const session = resultObj.session;
      
      if (resultObj.success && session) {
        store.dispatch(addSession(session));
        store.dispatch(setCurrentSession(session));
        // Server may return status 'idle' here while the agent is still starting.
        // Keep setStreaming(true) optimistic; the 'complete'/'error' WS listener will clear it.
        return session;
      }

      store.dispatch(setStreaming(false));
      console.error('Failed to start session:', resultObj.error || 'Unknown error');
      return null;
    } catch (error) {
      store.dispatch(setStreaming(false));
      console.error('Failed to start session:', error);
      return null;
    }
  }

  async continueSession(options: CoworkContinueOptions): Promise<boolean> {
    if (!this.transport) {
      console.error('Transport not initialized');
      return false;
    }

    store.dispatch(setStreaming(true));
    store.dispatch(updateSessionStatus({ sessionId: options.sessionId, status: 'running' }));

    const result = await this.transport.continueSession({
      sessionId: options.sessionId,
      prompt: options.prompt,
      systemPrompt: options.systemPrompt,
      activeSkillIds: options.activeSkillIds,
      imageAttachments: options.imageAttachments,
      audioAttachments: options.audioAttachments,
      fileAttachments: options.fileAttachments,
    });
    if (!result.success) {
      store.dispatch(setStreaming(false));
      store.dispatch(updateSessionStatus({ sessionId: options.sessionId, status: 'error' }));
      console.error('Failed to continue session:', result.error);
      return false;
    }

    return true;
  }

  async stopSession(sessionId: string): Promise<boolean> {
    if (!this.transport) return false;

    const result = await this.transport.stopSession(sessionId);
    if (result.success) {
      store.dispatch(setStreaming(false));
      store.dispatch(updateSessionStatus({ sessionId, status: 'idle' }));
      return true;
    }

    console.error('Failed to stop session:', result.error);
    return false;
  }

  async deleteSession(sessionId: string): Promise<boolean> {
    if (!this.transport) return false;

    const result = await this.transport.deleteSession(sessionId);
    if (result.success) {
      store.dispatch(deleteSessionAction(sessionId));
      return true;
    }

    console.error('Failed to delete session:', result.error);
    return false;
  }

  async deleteSessions(sessionIds: string[]): Promise<boolean> {
    if (!this.transport) return false;

    const result = await this.transport.deleteSessions(sessionIds);
    if (result.success) {
      store.dispatch(deleteSessionsAction(sessionIds));
      return true;
    }

    console.error('Failed to batch delete sessions:', result.error);
    return false;
  }

  async setSessionPinned(sessionId: string, pinned: boolean): Promise<boolean> {
    if (!this.transport) return false;

    const result = await this.transport.setSessionPinned(sessionId, pinned);
    if (result.success) {
      store.dispatch(updateSessionPinned({ sessionId, pinned }));
      return true;
    }

    console.error('Failed to update session pin:', result.error);
    return false;
  }

  async renameSession(sessionId: string, title: string): Promise<boolean> {
    if (!this.transport) return false;

    const normalizedTitle = title.trim();
    if (!normalizedTitle) return false;

    const result = await this.transport.renameSession(sessionId, normalizedTitle);
    if (result.success) {
      store.dispatch(updateSessionTitle({ sessionId, title: normalizedTitle }));
      return true;
    }

    console.error('Failed to rename session:', result.error);
    return false;
  }

  async exportSessionResultImage(_options: {
    rect: { x: number; y: number; width: number; height: number };
    defaultFileName?: string;
  }): Promise<{ success: boolean; canceled?: boolean; path?: string; error?: string }> {
    return { success: false, error: 'Image export not available in web mode' };
  }

  async captureSessionImageChunk(_options: {
    rect: { x: number; y: number; width: number; height: number };
  }): Promise<{ success: boolean; width?: number; height?: number; pngBase64?: string; error?: string }> {
    return { success: false, error: 'Image capture not available in web mode' };
  }

  async saveSessionResultImage(options: {
    pngBase64: string;
    defaultFileName?: string;
  }): Promise<{ success: boolean; canceled?: boolean; path?: string; error?: string }> {
    try {
      const blob = new Blob(
        [Uint8Array.from(atob(options.pngBase64), c => c.charCodeAt(0))],
        { type: 'image/png' }
      );
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = options.defaultFileName || 'image.png';
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
      return { success: true };
    } catch (error) {
      return {
        success: false,
        error: error instanceof Error ? error.message : 'Failed to save image',
      };
    }
  }

  async loadSession(sessionId: string): Promise<CoworkSession | null> {
    if (!this.transport) return null;

    const result = await this.transport.getSession(sessionId);
    console.log('[CoworkService] loadSession result:', result);
    
    // Handle both response formats: { session } and { data: { session } }
    const session = (result as any)?.session || result?.data?.session;
    if (result.success && session) {
      store.dispatch(setCurrentSession(session));
      store.dispatch(setStreaming(false));
      return session;
    }

    console.error('Failed to load session:', (result as any)?.error || result.error);
    return null;
  }

  async respondToPermission(requestId: string, result: CoworkPermissionResult): Promise<boolean> {
    if (!this.transport) return false;

    const response = await this.transport.respondToPermission(requestId, result);
    if (response.success) {
      store.dispatch(dequeuePendingPermission({ requestId }));
      return true;
    }

    console.error('Failed to respond to permission:', response.error);
    return false;
  }

  async updateConfig(config: CoworkConfigUpdate): Promise<boolean> {
    if (!this.transport) return false;

    const result = await this.transport.setConfig(config);
    if (result.success) {
      const currentConfig = store.getState().cowork.config;
      store.dispatch(setConfig({ ...currentConfig, ...config }));
      return true;
    }

    console.error('Failed to update config:', result.error);
    return false;
  }

  async getApiConfig(): Promise<CoworkApiConfig | null> {
    return this.transport?.getApiConfig?.() ?? null;
  }

  async checkApiConfig(options?: { probeModel?: boolean }): Promise<{ hasConfig: boolean; config: CoworkApiConfig | null; error?: string } | null> {
    return this.transport?.checkApiConfig?.(options) ?? null;
  }

  async saveApiConfig(config: CoworkApiConfig): Promise<{ success: boolean; error?: string } | null> {
    return this.transport?.saveApiConfig?.(config) ?? null;
  }

  async getSandboxStatus(): Promise<CoworkSandboxStatus | null> {
    return this.transport?.getSandboxStatus?.() ?? null;
  }

  async installSandbox(): Promise<{ success: boolean; status: CoworkSandboxStatus; error?: string } | null> {
    return this.transport?.installSandbox?.() ?? null;
  }

  async listMemoryEntries(input: {
    query?: string;
    status?: 'created' | 'stale' | 'deleted' | 'all';
    includeDeleted?: boolean;
    limit?: number;
    offset?: number;
  }): Promise<CoworkUserMemoryEntry[]> {
    const result = await this.transport?.listMemoryEntries(input);
    if (!result?.success || !result.data?.entries) return [];
    return result.data.entries;
  }

  async createMemoryEntry(input: {
    text: string;
    confidence?: number;
    isExplicit?: boolean;
  }): Promise<CoworkUserMemoryEntry | null> {
    const result = await this.transport?.createMemoryEntry(input);
    if (!result?.success || !result.data?.entry) return null;
    return result.data.entry;
  }

  async updateMemoryEntry(input: {
    id: string;
    text?: string;
    confidence?: number;
    status?: 'created' | 'stale' | 'deleted';
    isExplicit?: boolean;
  }): Promise<CoworkUserMemoryEntry | null> {
    const result = await this.transport?.updateMemoryEntry(input);
    if (!result?.success || !result.data?.entry) return null;
    return result.data.entry;
  }

  async deleteMemoryEntry(input: { id: string }): Promise<boolean> {
    const result = await this.transport?.deleteMemoryEntry(input);
    return Boolean(result?.success);
  }

  async getMemoryStats(): Promise<CoworkMemoryStats | null> {
    const result = await this.transport?.getMemoryStats();
    if (!result?.success || !result.data?.stats) return null;
    return result.data.stats;
  }

  onSandboxDownloadProgress(callback: (progress: CoworkSandboxProgress) => void): () => void {
    return this.transport?.onSandboxDownloadProgress?.(callback) ?? (() => {});
  }

  async generateSessionTitle(prompt: string | null): Promise<string | null> {
    return this.transport?.generateSessionTitle?.(prompt) ?? null;
  }

  async getRecentCwds(limit?: number): Promise<string[]> {
    return this.transport?.getRecentCwds?.(limit) ?? [];
  }

  clearSession(): void {
    store.dispatch(clearCurrentSession());
  }

  // Get connection status
  getConnectionStatus(): { connected: boolean; type: 'websocket'; serverUrl?: string } {
    return this.transport?.getConnectionStatus() ?? { connected: false, type: 'websocket' };
  }

  // Check if transport is available
  static isTransportAvailable(): boolean {
    return typeof WebSocket !== 'undefined';
  }

  destroy(): void {
    this.cleanupListeners();
    this.initialized = false;
    if (this.transport) {
      this.transport.destroy();
      this.transport = null;
    }
    resetTransport();
  }
}

export const coworkService = new CoworkService();
